import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatError, WASTE_TYPES, wasteLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { useConfig } from "@/context/ConfigContext";
import { Check, X, Save, ArrowLeft, Sparkles, ScanBarcode, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function OperatorReview() {
  const { id } = useParams();
  const nav = useNavigate();
  const cfg = useConfig();
  const [sub, setSub] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [weight, setWeight] = useState("");
  const [type, setType] = useState("");
  const [barcode, setBarcode] = useState("");
  const [noteInt, setNoteInt] = useState("");
  const [notePub, setNotePub] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    (async () => {
      try {
        const { data } = await api.get(`/submissions/${id}`);
        setSub(data);
        setWeight(data.verified_weight_kg ?? data.ai?.weight_kg ?? "");
        setType(data.waste_type);
        setBarcode(data.barcode_full || data.ai?.barcode || "");
        setNoteInt(data.note_internal ?? "");
        setNotePub(data.note_public ?? "");
        const token = localStorage.getItem("rifiuti_token");
        const r = await api.get(`/submissions/${id}/photo?auth=${token}`, { responseType: "blob" });
        objectUrl = URL.createObjectURL(r.data);
        setPhotoUrl(objectUrl);
      } catch (e) { toast.error(formatError(e)); }
    })();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);

  const save = async (nextStatus) => {
    setBusy(true);
    try {
      const body = {
        verified_weight_kg: weight ? parseFloat(weight) : null,
        waste_type: type,
        note_internal: noteInt || null,
        note_public: notePub || null,
      };
      // Only send barcode if it changed and is 8 digits
      if (barcode && /^\d{8}$/.test(barcode) && barcode !== sub.barcode_full) {
        body.barcode_code = barcode;
      }
      if (nextStatus) body.status = nextStatus;
      const { data } = await api.patch(`/submissions/${id}`, body);
      setSub(data);
      toast.success(nextStatus === "approved" ? "Conferimento approvato" : nextStatus === "rejected" ? "Conferimento contestato" : "Modifiche salvate");
      if (nextStatus) nav("/operator");
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  if (!sub) return <Layout><div className="text-sm text-muted-foreground">Caricamento…</div></Layout>;

  return (
    <Layout
      title="Revisione conferimento"
      subtitle={`${sub.user_name} · ${new Date(sub.created_at).toLocaleString("it-IT")}`}
      actions={<Link to="/operator"><Button variant="ghost" className="gap-2" data-testid="back-queue"><ArrowLeft className="w-4 h-4" /> Torna alla coda</Button></Link>}
    >
      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-3 overflow-hidden">
          <CardContent className="p-0">
            {photoUrl ? (
              <img src={photoUrl} alt="conferimento" className="w-full max-h-[700px] object-contain bg-gray-100" data-testid="rev-photo" />
            ) : (
              <div className="h-96 flex items-center justify-center text-muted-foreground">Caricamento foto…</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Stato attuale</div>
              <StatusBadge status={sub.status} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Barcode attuale</div>
                <div className="font-mono">{sub.barcode_full || <span className="text-amber-600 font-sans text-xs">non assegnato</span>}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Utente</div>
                <div className="font-medium text-xs">{sub.user_email}</div>
              </div>
            </div>

            {sub.ai && sub.ai.enabled && (
              <div className="p-3 rounded-lg bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100 text-purple-900 text-xs space-y-1">
                <div className="flex items-center gap-1.5 font-semibold"><Sparkles className="w-3.5 h-3.5" /> Lettura AI ({sub.ai.model})</div>
                <div>Peso: <strong>{sub.ai.weight_kg != null ? `${sub.ai.weight_kg} kg` : "non letto"}</strong> · confidence {Math.round((sub.ai.weight_confidence || 0) * 100)}%</div>
                <div>Barcode: <strong className="font-mono">{sub.ai.barcode || "non letto"}</strong> · confidence {Math.round((sub.ai.barcode_confidence || 0) * 100)}% · <span className="font-mono">{sub.ai.barcode_status}</span></div>
                {sub.ai.notes && <div className="italic pt-1 border-t border-purple-200">{sub.ai.notes}</div>}
                {sub.ai.error && <div className="text-red-800 italic">Errore AI: {sub.ai.error}</div>}
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600 flex items-center gap-1"><ScanBarcode className="w-3 h-3" /> Codice a barre (8 cifre)</Label>
              <Input inputMode="numeric" maxLength={8} value={barcode} onChange={(e) => setBarcode(e.target.value.replace(/\D/g, ""))} placeholder="00000000" className="mt-1.5 font-mono tracking-wider" data-testid="rev-barcode" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Peso verificato (kg)</Label>
              <Input type="number" step="0.01" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} className="mt-1.5" data-testid="rev-weight" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Tipologia</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1.5" data-testid="rev-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WASTE_TYPES.map((w) => {
                    const enabled = (cfg.waste_types_enabled || []).includes(w.value);
                    return (
                      <SelectItem key={w.value} value={w.value}>
                        {w.label}{!enabled && <span className="text-xs text-muted-foreground ml-2">(disabilitata)</span>}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Nota per l'utente (visibile)</Label>
              <Textarea rows={2} value={notePub} onChange={(e) => setNotePub(e.target.value)} className="mt-1.5" data-testid="rev-note-public" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-600">Nota interna (non visibile)</Label>
              <Textarea rows={2} value={noteInt} onChange={(e) => setNoteInt(e.target.value)} className="mt-1.5" data-testid="rev-note-internal" />
            </div>

            <div className="grid grid-cols-3 gap-2 pt-2">
              <Button variant="outline" onClick={() => save(null)} disabled={busy} data-testid="rev-save" className="rounded-full gap-1"><Save className="w-4 h-4" /> Salva</Button>
              <Button variant="destructive" onClick={() => save("rejected")} disabled={busy} data-testid="rev-reject" className="rounded-full gap-1"><X className="w-4 h-4" /> Contesta</Button>
              <Button onClick={() => save("approved")} disabled={busy} data-testid="rev-approve" className="rounded-full gap-1 bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4" /> Approva</Button>
            </div>
            {(sub.status === "rejected" || sub.status === "approved") && !sub.tari_batch_id && (
              <div className="pt-2 border-t border-gray-200">
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 mb-2">
                  {sub.status === "rejected" ? "Conferimento contestato. Puoi riesaminarlo dopo colloquio al Comune." : "Conferimento già approvato. Puoi ancora modificarlo o riportarlo in coda per rilettura."}
                </div>
                <Button variant="outline" onClick={() => save("pending")} disabled={busy} data-testid="rev-reopen" className="w-full rounded-full gap-1 border-amber-300 text-amber-900 hover:bg-amber-50">
                  <RotateCcw className="w-4 h-4" /> Riporta in attesa (riesame)
                </Button>
              </div>
            )}
            <Link to={`/operator/user/${sub.user_id}`} className="block text-center text-sm text-primary hover:underline pt-2" data-testid="rev-user-link">
              Vedi tutti i conferimenti di {sub.user_name} →
            </Link>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
